import { useMutation, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { track } from '@/modules/Analytics';
import { Account } from '@/types/types';

/** Fields that are sent as JSON (not multipart) */
type AppAccessPayload = { has_app_access: false } | { has_app_access: true; email: string };

export type UpdateAccountPayload =
  | (Omit<Partial<Account>, 'file'> & { file?: File | Blob })
  | AppAccessPayload;

export const useUpdateAccount = (accountId: string | number) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (account: UpdateAccountPayload) => {
      // has_app_access toggle — send as JSON PATCH
      if ('has_app_access' in account) {
        const { data } = await axiosInstance.patch<Account>('/api/v1/accounts/' + accountId, {
          account,
        });
        return data;
      }

      // All other updates — send as multipart/form-data
      const formData = new FormData();
      if (account.file) {
        formData.append('account[photo]', account.file);
      }

      if (account.name) {
        formData.append('account[name]', account.name);
      }

      if (account.hourly_rate) {
        formData.append('account[hourly_rate]', account.hourly_rate.toString());
      }

      if (account.email) {
        formData.append('account[email]', account.email);
      }

      if (account.role) {
        formData.append('account[role]', account.role);
      }

      if ('company_account_category_id' in account) {
        formData.append(
          'account[company_account_category_id]',
          account.company_account_category_id?.toString() ?? ''
        );
      }

      if (account.marketing_opt_in) {
        formData.append('account[marketing_opt_in]', account.marketing_opt_in.toString());
      }

      if (account.terms_and_conditions_accepted) {
        formData.append(
          'account[terms_and_conditions_accepted]',
          account.terms_and_conditions_accepted.toString()
        );
      }

      const { data } = await axiosInstance.patch<Account>(
        '/api/v1/accounts/' + accountId,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    },
    onSuccess: (_data, account) => {
      if ('has_app_access' in account && account.has_app_access) track('user_invited');
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts._def,
      });
    },
  });
};

export default useUpdateAccount;
